import { translate as t } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { getCurrentOrgName, getSessionInfo, isAiAnswerGenerationEnabledForUser, isOrgUiEnabled, isSpotterExperienceEnabled, setSessionInfo, } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getConfigInfoOverrides, updateConfigInfo, } from "../../../services/system/system-service";
import { HomepageExperienceToggle } from './homepage-experience-toggle';
import styles from './homepage-experience-toggle.module.scss';
export const HomepageExperienceToggleContainer = ({ orgViewModeType, onEditingChange, onLoadingChange, onSavingChange, onShowToast, onVersionChange, }) => {
    const isAllOrgContext = !isOrgUiEnabled() || orgViewModeType === 'all-org';
    const getOrgLabel = () => isAllOrgContext ? 'All Orgs' : getCurrentOrgName();
    const isMountedRef = useRef(true);
    const [isV4Enabled, setIsV4Enabled] = useState(false);
    const [pendingValue, setPendingValue] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                setLoading(true);
                const config = await getConfigInfoOverrides();
                const current = Boolean(config.isHomepageV4Enabled) &&
                    isSpotterExperienceEnabled() &&
                    isAiAnswerGenerationEnabledForUser();
                if (!isMountedRef.current)
                    return;
                setIsV4Enabled(current);
                setPendingValue(current);
            }
            catch (_error) {
            }
            finally {
                if (isMountedRef.current) {
                    setLoading(false);
                    onLoadingChange?.(false);
                }
            }
        };
        fetchConfig();
    }, []);
    useEffect(() => {
        onSavingChange?.(saving);
    }, [saving, onSavingChange]);
    const handleSave = useCallback(async (newValue) => {
        try {
            setSaving(true);
            await updateConfigInfo({ isHomepageV4Enabled: newValue });
            if (!isMountedRef.current) {
                return;
            }
            setIsV4Enabled(newValue);
            setPendingValue(newValue);
            onVersionChange?.(newValue);
            const existingSessionInfo = getSessionInfo();
            if (existingSessionInfo) {
                setSessionInfo({
                    ...existingSessionInfo,
                    configInfo: {
                        ...existingSessionInfo.configInfo,
                        isHomepageV4Enabled: newValue,
                    },
                });
            }
            collectEvent(new UserAction(userActionTypeEnum.HOMEPAGE_EXPERIENCE_CHANGE_SAVE), {
                homepageExperience: newValue ? 'v4' : 'v3',
                org: getOrgLabel(),
            });
            setIsEditing(false);
            onEditingChange?.(false);
            onShowToast?.(t('tscli.ui.appsettings.datasave.success.message'));
        }
        catch (_error) {
            console.error('HomepageExperience save error:', _error);
        }
        finally {
            if (isMountedRef.current) {
                setSaving(false);
            }
        }
    }, [onEditingChange, onShowToast, onVersionChange]);
    const startEditing = useCallback(() => {
        if (saving)
            return;
        collectEvent(new UserAction(userActionTypeEnum.CLICK), {
            trackId: 'Homepage experience',
            org: getOrgLabel(),
        });
        setPendingValue(isV4Enabled);
        setIsEditing(true);
        onEditingChange?.(true);
    }, [isV4Enabled, saving, onEditingChange]);
    const cancelEditing = useCallback(() => {
        setPendingValue(isV4Enabled);
        setIsEditing(false);
        onEditingChange?.(false);
    }, [isV4Enabled, onEditingChange]);
    const handleValueChange = useCallback((value) => {
        if (value &&
            (!isAiAnswerGenerationEnabledForUser() ||
                !isSpotterExperienceEnabled())) {
            setShowErrorModal(true);
            return;
        }
        setPendingValue(value);
    }, []);
    const handleSaveClick = useCallback(() => {
        handleSave(pendingValue);
    }, [handleSave, pendingValue]);
    const CONTACT_SUPPORT_URL = 'https://www.thoughtspot.com/contact';
    if (loading) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(HomepageExperienceToggle, { isV4Enabled: isV4Enabled, pendingValue: pendingValue, onValueChange: handleValueChange, onSave: handleSaveClick, onEdit: startEditing, onCancel: cancelEditing, isEditing: isEditing, saving: saving }),
        showErrorModal && (React.createElement(Modal, { size: ModalSize.SMALL, title: t('adminSection.homepageExperience.errorModal.title'), isOpen: showErrorModal, confirmButton: {
                text: t('adminSection.homepageExperience.errorModal.close'),
                onConfirm: () => setShowErrorModal(false),
            }, dismissButton: {
                text: t('adminSection.homepageExperience.errorModal.contactSupport'),
                onClick: () => {
                    window.open(CONTACT_SUPPORT_URL, '_blank');
                    setShowErrorModal(false);
                },
            }, onDismiss: () => setShowErrorModal(false) },
            React.createElement("div", { className: styles.errorModalBody },
                React.createElement(Typography, { variant: "body-normal" }, t('adminSection.homepageExperience.errorModal.body')))))));
};
//# sourceMappingURL=homepage-experience-toggle.container.js.map